import * as nodePath from 'node:path';
import { DirectUpload } from '@rails/activestorage';
import type { FileSystemTree, PathWatcherEvent, WebContainer } from '@webcontainer/api';
import { atom, computed, map } from 'nanostores';
import { toast } from 'react-toastify';
import {
  fileMapCache,
  filterFileMap,
  walkFileMap,
  writeDirentRecursively,
  type Dirent,
  type DirentBase,
  type File,
  type FileMap,
  type Folder,
  type UnrestrictedDirent,
} from './filemap';
import { applyFileUpdates, decodeFileContent } from './filemap/apply-file-updates';
import { saveProject } from '~/lib/.client/api/projects';
import { clientFetch } from '~/lib/.client/fetch';
import {
  BOLT_MODIFICATIONS,
  CLIENT_ORIGIN,
  SUPABASE_EDGE_FUNCTIONS_FOLDER,
  SUPABASE_MIGRATIONS_FOLDER,
} from '~/lib/constants';
import { getFramework } from '~/lib/persistence/frameworks';
import type { AppFiles } from '~/lib/projects';
import { forkProject } from '~/lib/projects/forkProject';
import { HIDDEN_FOLDERS_NOT_IGNORED, isFilePathIgnored } from '~/lib/stores/files/ignored-files';
import { projectStore } from '~/lib/stores/project';
import { getToken } from '~/lib/stores/token';
import { userStore } from '~/lib/stores/user';
import { workbenchStore, type WorkbenchWritableFile } from '~/lib/stores/workbench';
import { setCORSProxy } from '~/lib/webcontainer/cors-proxy';
import { bufferWatchEvents } from '~/utils/buffer';
import { WORK_DIR } from '~/utils/constants';
import { debounceAndQueue } from '~/utils/debounce';
import { hashText } from '~/utils/hash';
import { hmrPropertyFactory } from '~/utils/hmr';
import { invariant } from '~/utils/invariant';
import { createScopedLogger } from '~/utils/logger';
import { derived, waitUntil } from '~/utils/nanostore';
import { removeTrailingSlashes, sanitizePath } from '~/utils/path';
import { withResolvers } from '~/utils/promises';
import { unreachable } from '~/utils/unreachable';
import { addWorkDir, removeWorkDir } from '~/utils/workDir';

const hmr = hmrPropertyFactory('FilesStore');

const logger = createScopedLogger('FilesStore');

const STORAGE_API = `${CLIENT_ORIGIN}/storage`;

export type { Dirent, File, FileMap, Folder, UnrestrictedDirent };

export interface CreateDirectoryOptions {
  errorIfExists?: boolean;
  errorIfIgnored?: boolean;
}

export interface WriteFileOptions {
  errorIfExists?: boolean;
  errorIfIgnored?: boolean;
  encoding?: string | null;
}

export interface ReadFileOptions {
  errorIfNotExists?: boolean;
}

export interface StackBlitzResourceConfig {
  corsProxy?: boolean;
  [key: string]: any;
}

export interface ContentHashOptions {
  files?: FileMap;
  signal?: AbortSignal;
}

export class FilesystemError extends Error {
  constructor(
    message: string,
    readonly code: 'EEXISTS' | 'UNKNOWN' = 'UNKNOWN',
  ) {
    super(message);
  }
}

export class FilesStore {
  #webcontainer: Promise<WebContainer>;

  /**
   * Tracks the number of files without folders.
   */
  @hmr()
  #size = 0;

  /**
   * Map of files that matches the state of WebContainer.
   */
  @hmr()
  files = map<FileMap>({});

  /**
   * Map of pending file write callbacks waiting for file watcher events.
   */
  #pendingFileResolvers = new Map<string, PromiseWithResolvers<void>>();

  listenToFilesChanges = atom<boolean>(true);

  /**
   * A reactive FileMap of all the locked files.
   */
  lockedFiles = derived(this.files, fileMapCache(), (cache, files) => {
    return filterFileMap(
      files,
      (fileOrFolder, parentMatched) => fileOrFolder?.isLocked || (parentMatched && !fileOrFolder?.isTargeted),
      cache,
    );
  });

  /**
   * A reactive FileMap of all the targeted files.
   */
  targetedFiles = derived(this.files, fileMapCache(), (cache, files) => {
    return filterFileMap(
      files,
      (fileOrFolder, parentMatched) => fileOrFolder?.isTargeted || (parentMatched && !fileOrFolder?.isLocked),
      cache,
    );
  });

  /**
   * A reactive FileMap of all the migration files.
   */
  migrationFiles = derived(this.files, fileMapCache(), (cache, files) => {
    return filterFileMap(
      files,
      (fileOrFolder, _, fullPath) => {
        return fileOrFolder?.type === 'file' && removeWorkDir(fullPath).startsWith(SUPABASE_MIGRATIONS_FOLDER + '/');
      },
      cache,
    );
  });

  /**
   * A reactive FileMap of all the files in the supabase/functions folder.
   */
  edgeFunctionsFiles = derived(this.files, fileMapCache(), (cache, files) => {
    return filterFileMap(
      files,
      (fileOrFolder, _, fullPath) => {
        return (
          fileOrFolder?.type === 'file' && removeWorkDir(fullPath).startsWith(SUPABASE_EDGE_FUNCTIONS_FOLDER + '/')
        );
      },
      cache,
    );
  });

  /**
   * Tracks whether the persisted IDB file store matches the current FS state.
   */
  hasPersistedChanges = true;

  /**
   * Tracks number of binary files that are uploading.
   */
  binaryUploads = 0;

  /**
   * Promise that resolves when the project import is complete.
   */
  waitForImport: Promise<void> = Promise.resolve();

  fileImportComplete = true;

  get filesCount() {
    return this.#size;
  }

  packageJson = computed(this.files, (files) => {
    const packageJsonPath = addWorkDir('package.json');
    const packageJsonFile = files[packageJsonPath];

    if (packageJsonFile?.type !== 'file') {
      return undefined;
    }

    try {
      return JSON.parse(packageJsonFile.content);
    } catch {
      return undefined;
    }
  });

  allPackageJsonFiles = derived(this.files, fileMapCache(), (cache, files) => {
    const packageJsonFiles: Array<{ path: string; content: any }> = [];

    const filteredFiles = filterFileMap(
      files,
      (fileOrFolder, _, fullPath) => {
        return fileOrFolder?.type === 'file' && fullPath.endsWith('/package.json');
      },
      cache,
    );

    for (const [fullPath, dirent] of Object.entries(filteredFiles)) {
      if (dirent?.type === 'file') {
        try {
          const content = JSON.parse(dirent.content);
          packageJsonFiles.push({ path: removeWorkDir(fullPath), content });
        } catch {}
      }
    }

    return packageJsonFiles;
  });

  // tracks the framework used in the project
  framework = computed(this.packageJson, (packageJson) => {
    if (!packageJson) {
      return undefined;
    }

    return getFramework(packageJson);
  });

  /**
   * In-memory version of .stackblitzrc config.
   */
  @hmr()
  stackBlitzResourceConfigFile = atom<StackBlitzResourceConfig>({ corsProxy: true });

  constructor(webcontainerPromise: Promise<WebContainer>) {
    this.#webcontainer = webcontainerPromise;

    const disposeConfigSubscription = this.stackBlitzResourceConfigFile.subscribe((config) => {
      setCORSProxy(Boolean(config.corsProxy));
    });

    if (import.meta.hot) {
      import.meta.hot.data.disposeConfigSubscription?.();
      import.meta.hot.data.disposeConfigSubscription = disposeConfigSubscription;
    }

    this.#init();
  }

  getFile(filePath: string, options?: ReadFileOptions) {
    const dirent = this.files.get()[filePath];

    if (!dirent && options?.errorIfNotExists) {
      throw new FilesystemError('File does not exist');
    }

    if (dirent?.type !== 'file') {
      return undefined;
    }

    return dirent;
  }

  writeStackBlitzResourceConfig(config: StackBlitzResourceConfig) {
    if (projectStore.shouldFork() && !projectStore.isForking.get()) {
      const slug = projectStore.getSlug();

      invariant(slug);

      forkProject();
    }

    const files = this.files.get();

    const hasStackBlitzRC = !!files[addWorkDir('.stackblitzrc')];
    const hasStackBlitzConfig = !!files[addWorkDir('.stackblitz/config.json')];

    if (hasStackBlitzRC) {
      this.writeFile('.stackblitzrc', {
        content: JSON.stringify(config, null, 2),
        type: 'file',
      });
    }

    if (hasStackBlitzConfig || !hasStackBlitzRC) {
      this.writeFile('.stackblitz/config.json', {
        content: JSON.stringify(config, null, 2),
        type: 'file',
      });
    }
  }

  assignStackBlitzResourceConfig(config: StackBlitzResourceConfig) {
    this.writeStackBlitzResourceConfig(Object.assign(this.stackBlitzResourceConfigFile.get(), config));
  }

  isAIAllowedToEdit(filePath: string) {
    const lockedFiles = this.lockedFiles.get();
    const targetedFiles = this.targetedFiles.get();

    /**
     * We include folders for those numbers, as the AI is allowed
     * to add files to targeted folders and is not allowed to add
     * files to locked folders.
     */
    const lockedFilesCount = Object.keys(lockedFiles).length;
    const targetedFilesCount = Object.keys(targetedFiles).length;

    // early exit for the most common path
    if (lockedFilesCount === 0 && targetedFilesCount === 0) {
      return true;
    }

    const fullPath = addWorkDir(removeTrailingSlashes(filePath));
    const parentDirPath = nodePath.dirname(fullPath);

    if (targetedFilesCount > 0) {
      return fullPath in targetedFiles || parentDirPath in targetedFiles;
    }

    if (lockedFilesCount > 0) {
      return !(fullPath in lockedFiles) && !(parentDirPath in lockedFiles);
    }

    return true;
  }

  removeFromTargetOrLockedFiles(filePath: string) {
    this.#setBooleanProperties(filePath, {
      isLocked: false,
      isTargeted: false,
    });
  }

  setFileOrFolderTarget(filePath: string, isTargeted: boolean) {
    this.#setBooleanProperties(filePath, {
      isTargeted,
      isLocked: false,
    });
  }

  setFileOrFolderLocked(filePath: string, isLocked: boolean) {
    this.#setBooleanProperties(filePath, {
      isTargeted: false,
      isLocked,
    });
  }

  resetAllLockedTargetedFiles() {
    const allFiles = this.files.get();

    const nonDeletedEntries = Object.entries(allFiles).filter(
      (entry): entry is [string, Dirent] => entry[1] !== undefined,
    );

    this.files.set(
      Object.fromEntries(
        nonDeletedEntries.map(([filePath, fileEntry]) => [
          filePath,
          {
            ...fileEntry,
            isLocked: false,
            isTargeted: false,
          },
        ]),
      ),
    );
  }

  #setBooleanProperties(filePath: string, properties: Partial<DirentBase>) {
    const allFiles = this.files.get();
    const fullPath = addWorkDir(removeTrailingSlashes(filePath));
    const fileOrFolder = allFiles[fullPath];

    if (!fileOrFolder) {
      return;
    }

    if (fileOrFolder.type === 'file') {
      this.files.setKey(fullPath, {
        ...fileOrFolder,
        ...properties,
      });

      return;
    }

    const newFiles = { ...allFiles };

    for (const { path, dirent } of walkFileMap(allFiles, fullPath)) {
      if (dirent?.type === 'file') {
        newFiles[path] = {
          ...dirent,
          ...properties,
        };
      }
    }

    this.files.set(newFiles);
  }

  async rename(fromFilePath: string, toFilePath: string) {
    const fromSanitizedFilePath = removeTrailingSlashes(fromFilePath);
    const toSanitizedFilePath = removeTrailingSlashes(toFilePath);

    const fromFullPath = addWorkDir(fromSanitizedFilePath);
    const toFullPath = addWorkDir(toSanitizedFilePath);

    const allFiles = this.files.get();

    const fromFile = allFiles[fromFullPath];

    if (!fromFile) {
      throw new FilesystemError(`Rename: '${fromSanitizedFilePath}' does not exists`);
    }

    if (toFullPath in allFiles) {
      throw new FilesystemError(`Rename: '${toSanitizedFilePath}' already exists`);
    }

    if (toFullPath.startsWith(`${fromFullPath}/`)) {
      throw new FilesystemError(`Rename: '${fromSanitizedFilePath}' cannot be moved inside itself`);
    }

    const parentDir = nodePath.dirname(toFullPath);

    if (parentDir !== WORK_DIR && !(parentDir in allFiles)) {
      throw new FilesystemError(
        `Cannot rename '${fromSanitizedFilePath}' to '${toSanitizedFilePath}' as directory '${removeWorkDir(parentDir)}' does not exists`,
      );
    }

    if (isFilePathIgnored({ fullPath: toFullPath, isDirectory: fromFile.type === 'folder' })) {
      throw new FilesystemError(`Cannot create '${toFullPath}': operation not allowed.`);
    }

    const newFiles: FileMap = {};

    for (const [direntPath, value] of Object.entries(allFiles)) {
      if (direntPath === fromFullPath) {
        newFiles[toFullPath] = value;
      } else if (direntPath.startsWith(`${fromFullPath}/`)) {
        const newPath = toFullPath + direntPath.slice(fromFullPath.length);

        newFiles[newPath] = value;
      } else if (value) {
        newFiles[direntPath] = value;
      }
    }

    this.files.set(newFiles);

    const webcontainer = await this.#webcontainer;

    await webcontainer.fs.rename(fromSanitizedFilePath, toSanitizedFilePath);
  }

  cut(fromFilePath: string, toFilePath: string, kind: 'file' | 'folder') {
    const uniqueToPath = this.generateUniqueName(toFilePath, kind);

    return this.rename(fromFilePath, uniqueToPath);
  }

  async writeFile(argFilePath: string, file: WorkbenchWritableFile, options?: WriteFileOptions) {
    const filePath = removeWorkDir(argFilePath);
    const sanitizedPath = removeTrailingSlashes(filePath);
    const fullPath = addWorkDir(sanitizedPath);

    if (options?.errorIfExists && fullPath in this.files.get()) {
      throw new FilesystemError('File already exists', 'EEXISTS');
    }

    if (options?.errorIfIgnored && isFilePathIgnored({ fullPath, isDirectory: false })) {
      throw new FilesystemError(`Cannot create '${filePath}': operation not allowed.`);
    }

    const resolver = withResolvers<void>();

    if (file.isBinary) {
      const pendingFileResolver = this.#pendingFileResolvers.get(fullPath);

      await pendingFileResolver?.promise;

      this.#pendingFileResolvers.set(fullPath, resolver);
    } else {
      resolver.resolve();
    }

    /**
     * Preemptively set the file in the store to prevent race conditions with file actions.
     * This helps accurately determine if a file is being created or updated. Normally, files
     * are only stored when the file watcher triggers, but relying solely on that can lead to
     * timing issues.
     *
     * Our in memory copy uses absolute paths.
     */
    if (typeof file.content === 'string' || !file.isBinary) {
      const fullPath = addWorkDir(sanitizedPath);
      const prevFile = this.files.get()[fullPath];

      this.#createDirentRecursively(fullPath, {
        ...(file as File),
        content: typeof file.content === 'string' ? file.content : decodeFileContent(file.content),
        isLocked: prevFile?.isLocked ?? false,
        isTargeted: prevFile?.isTargeted ?? false,
        lastModified: Date.now(),
      });
    }

    const parentDirPath = nodePath.dirname(sanitizedPath);

    const webcontainer = await this.#webcontainer;

    // create parent folder if it does not exist
    await webcontainer.fs.mkdir(parentDirPath, { recursive: true });

    await webcontainer.fs.writeFile(sanitizedPath, file.content, { encoding: options?.encoding || null });

    return resolver.promise;
  }

  createInMemoryFileIfMissing(filePath: string) {
    if (!this.files.get()[filePath]) {
      this.files.setKey(filePath, {
        type: 'file',
        content: '',
        isBinary: false,
        isStackBlitzBinary: false,
        isLocked: false,
        isTargeted: false,
        lastModified: Date.now(),
      });
    }
  }

  async copyFile(fromFilePath: string, toFilePath: string) {
    const file = this.getFile(addWorkDir(fromFilePath), { errorIfNotExists: true });
    const filename = this.generateUniqueName(toFilePath, 'file');

    await this.writeFile(filename, file!);

    return filename;
  }

  async removeFile(filePath: string) {
    const sanitizedPath = removeTrailingSlashes(filePath);

    this.files.setKey(addWorkDir(sanitizedPath), undefined);

    const webcontainer = await this.#webcontainer;

    return webcontainer.fs.rm(sanitizedPath, { force: true });
  }

  async createDirectory(filePath: string, options?: CreateDirectoryOptions) {
    const sanitizedPath = removeTrailingSlashes(filePath);
    const fullPath = addWorkDir(sanitizedPath);

    if (options?.errorIfExists && fullPath in this.files.get()) {
      throw new FilesystemError('Folder already exists', 'EEXISTS');
    }

    if (options?.errorIfIgnored && isFilePathIgnored({ fullPath, isDirectory: true })) {
      throw new FilesystemError(`Cannot create '${filePath}': operation not allowed.`);
    }

    this.#createDirentRecursively(fullPath, { type: 'folder', isLocked: false, isTargeted: false });

    const webcontainer = await this.#webcontainer;

    return webcontainer.fs.mkdir(sanitizedPath, { recursive: true });
  }

  async copyDirectory(fromFilePath: string, toFilePath: string) {
    const folderPath = this.generateUniqueName(toFilePath, 'folder');
    const fullFromFilePath = addWorkDir(fromFilePath);
    const files = Object.entries(this.files.get());

    await this.createDirectory(folderPath);

    for (const [filePath, file] of files) {
      if (filePath === fullFromFilePath || !filePath.startsWith(`${fullFromFilePath}/`)) {
        continue;
      }

      const newName = removeWorkDir(filePath.replace(fromFilePath, folderPath));

      if (file?.type === 'file') {
        await this.writeFile(newName, file);
      } else {
        await this.createDirectory(newName);
      }
    }
  }

  async removeDirectory(filePath: string) {
    const sanitizedPath = removeTrailingSlashes(filePath);

    const fullPath = addWorkDir(sanitizedPath);

    // iterate over all files and remove those that are in the directory
    for (const { path } of walkFileMap(this.files.value ?? {}, fullPath)) {
      this.files.setKey(path, undefined);
    }

    const webcontainer = await this.#webcontainer;

    return webcontainer.fs.rm(sanitizedPath, { recursive: true, force: true });
  }

  async computeContentHash(filePath: string, { signal, files }: ContentHashOptions = {}): Promise<string> {
    let concatenatedHashes = '';
    let rounds = 0;

    for (const { dirent } of walkFileMap(files ?? this.files.get(), filePath)) {
      if (dirent.type === 'file') {
        const result = await hashText(dirent.content);

        signal?.throwIfAborted();

        concatenatedHashes += result;
        rounds += 1;
      }
    }

    /**
     * Only hash again if there's more than a single hash computed.
     *
     * This logic also makes it trivial to see when a folder is empty because
     * its hash will always be '' which is more self explanatory than
     * 'da39a3ee5e6b4b0d3255bfef95601890afd80709'.
     */
    return rounds > 1 ? hashText(concatenatedHashes) : concatenatedHashes;
  }

  async importProjectFiles(appFiles: AppFiles) {
    this.fileImportComplete = false;

    this.waitForImport = this.#webcontainer.then(async (webcontainer) => {
      // this is kept for backwards compatibility so we simply remove this legacy file
      delete appFiles[BOLT_MODIFICATIONS];

      const downloads = [];

      const newFiles = { ...this.files.get() };

      const mountTree: FileSystemTree = {};

      for (const appFile of Object.values(appFiles)) {
        try {
          const { fullPath: appFilesFullPath } = appFile;
          const filePath = removeWorkDir(appFilesFullPath);
          const sanitizedPath = sanitizePath(filePath);
          const fullPath = addWorkDir(sanitizedPath);

          if (appFile.type === 'file') {
            const { isBinary, contents, blobId } = appFile;

            writeDirentRecursively(newFiles, fullPath, {
              type: 'file',
              content: contents,
              isBinary,
              isStackBlitzBinary: isBinary || !!blobId,
              blobId,
              isLocked: false,
              isTargeted: false,
              lastModified: Date.now(),
            });

            /**
             * If the `appFile` has a `blobId` it's a special case. This means that the
             * file is not an actual binary file but a file that has a url in it as a string.
             * When the file is to be used, it's supposed to use that url to fetch the file
             * content from where the file is hosted.
             */
            if (!isBinary && !blobId) {
              insertIntoTree(mountTree, sanitizedPath, { file: { contents } });
            }

            if (isBinary || !!blobId) {
              if (contents.length === 0) {
                logger.error(`malformed project file: ${fullPath}`);
              } else {
                let request;

                if (contents.startsWith(STORAGE_API)) {
                  const fileURL = `/api/storage` + contents.slice(STORAGE_API.length);

                  request = clientFetch(fileURL);
                } else {
                  request = clientFetch(contents);
                }

                request = request
                  .then((res) => res.arrayBuffer())
                  .then((buffer) => {
                    return webcontainer.internal.writeFile(sanitizedPath, new Uint8Array(buffer), { emitEvent: false });
                  });

                downloads.push(request);
              }
            }
          } else {
            insertIntoTree(mountTree, fullPath, { directory: {} });
          }
        } catch (error: any) {
          toast.error(error && 'message' in error ? error.message : JSON.stringify(error));
        }
      }

      this.files.set(newFiles);

      try {
        await Promise.all([webcontainer.mount(mountTree, { unstableEmit: false }), ...downloads]);
      } catch (error: any) {
        if ('message' in error) {
          toast.error(error.message);
        }
      }

      this.fileImportComplete = true;
    });
  }

  async saveFile(filePath: string, content: string) {
    const webcontainer = await this.#webcontainer;

    /**
     * We need to wait for the import to complete. This way, we know
     * that a snapshot of the filesystem is not updated until the
     * initial import is complete.
     */
    await this.waitForImport;

    try {
      const relativePath = nodePath.relative(webcontainer.workdir, filePath);

      if (!relativePath) {
        throw new Error(`EINVAL: invalid file path, write '${relativePath}'`);
      }

      const oldFile = this.getFile(filePath);

      if (oldFile === undefined) {
        unreachable('Expected file to be defined');
      }

      /**
       * We immediately update the file and don't rely on the `change` event coming from the watcher
       * once we call `writeFile` below.
       */
      this.files.setKey(filePath, { ...oldFile, content });

      await webcontainer.fs.writeFile(relativePath, content);

      logger.debug('File updated');
    } catch (error) {
      logger.error(`Failed to update file content\n\n${error}`);

      throw error;
    }
  }

  #saveFileHistory(changed: string[]) {
    const files = this.files.get();

    // should persist if `changed` wasn't set or if any non-hidden files changed
    const shouldPersist = !changed.every((changePath) => {
      const splitPath = changePath.split('/');
      const filename = splitPath.pop() ?? '';
      const isDotfile = files[changePath]?.type === 'file' && filename.startsWith('.');
      const parentPath = splitPath.join('/');
      const isParentPathHidden = isHiddenFolder(parentPath);

      if (isDotfile && !isParentPathHidden) {
        return false;
      }

      return isParentPathHidden || isHiddenFolder(filename);
    });

    if (shouldPersist) {
      this.hasPersistedChanges = false;

      this.#persistProject();
    }
  }

  #createDirentRecursively(fullPath: string, value: Dirent) {
    const allFiles = this.files.get();
    const newFiles = { ...allFiles };

    writeDirentRecursively(newFiles, fullPath, value);

    this.files.set(newFiles);
  }

  #persistProject = debounceAndQueue(async (signal) => {
    const acceptCancellationIfAny = () => signal.throwIfAborted();
    const files = this.files.get();

    if (Object.keys(files).length === 0) {
      this.hasPersistedChanges = true;

      return;
    }

    await waitUntil(workbenchStore.isProcessing, false, { signal });
    await this.waitForImport;

    acceptCancellationIfAny();

    await waitUntil(projectStore.isForking, false, { signal });

    const editedAt = projectStore.getEditedAt();

    /**
     * When you import a project you don't have access to, it does not have an editedAt timestamp. We can't
     * save in that case as the call will fail.
     *
     * If the user has to fork, we don't perform a save either. We will fork just in time when the user triggers
     * an operation that actually warrants a save. Right now, saves are triggered cause we listen for changes
     * in the webcontainer fs with `watchPaths` and register it before the initial state is written. We should
     * probably update that logic at some point.
     */
    if (!editedAt || projectStore.shouldFork() || !userStore.getUser()) {
      this.hasPersistedChanges = true;

      return;
    }

    let slug = projectStore.getSlug();

    if (!slug) {
      slug = await projectStore.waitForSlug();
    }

    acceptCancellationIfAny();

    invariant(slug);

    try {
      // when the fetch fires here we no longer accept cancellation
      const response = await saveProject({
        slug,
        editedAt,
        files,
        conflictHandler: (lastEditor: string, editedAt: string) => {
          return projectStore.emit('conflict', lastEditor, editedAt);
        },
        fork: async () => projectStore.emit('fork'),
        projectTooBigToSave: () => projectStore.emit('project-too-big'),
      });

      if (response) {
        projectStore.setEditedAt(response.editedAt);
      }

      this.hasPersistedChanges = true;
    } catch (error) {
      console.error(error);

      toast.error(
        "Failed to save project. You can download your project by clicking the 'Download' button on top so you don't lose any changes.",
      );
    }
  });

  async #init() {
    const webcontainer = await this.#webcontainer;

    webcontainer.internal.watchPaths(
      {
        path: WORK_DIR,
        include: HIDDEN_FOLDERS_NOT_IGNORED,
        exclude: ['**/node_modules'],
        includeContent: true,
        ignoreHiddenFiles: false,
        ignoreHiddenFolders: false,
      },
      bufferWatchEvents(100, this.#processEventBuffer.bind(this)),
    );

    webcontainer.internal.watchPaths(
      {
        include: [`${WORK_DIR}/**`],
        exclude: ['**/node_modules', '.git'],
        includeContent: true,
        ignoreHiddenFiles: false,
        ignoreHiddenFolders: true,
      },
      bufferWatchEvents(100, this.#processEventBuffer.bind(this)),
    );

    this.files.subscribe(() => {
      if (!this.listenToFilesChanges.get()) {
        return;
      }

      this.hasPersistedChanges = false;

      this.#persistProject();
    });
  }

  #processEventBuffer(events: Array<[events: PathWatcherEvent[]]>) {
    const handleBinaryFileUpdate = (path: string, buffer: Uint8Array) => {
      const slug = projectStore.getSlug();

      const filename = path.slice(path.lastIndexOf('/'));

      const file = new File([buffer], filename) as FileWithPath;
      file.path = path;

      this.binaryUploads++;

      uploadBinaryFile(slug as string, filename, file)
        .then((blob) => {
          const previousVersion = this.files.get()[path];

          this.files.setKey(path, {
            type: 'file',
            content: blob.content,
            isBinary: true,
            isStackBlitzBinary: true,
            blobId: blob.signedId,
            isLocked: previousVersion?.isLocked ?? false,
            isTargeted: previousVersion?.isTargeted ?? false,
            lastModified: Date.now(),
          });

          const pendingFileResolver = this.#pendingFileResolvers.get(path);

          if (pendingFileResolver) {
            this.#pendingFileResolvers.delete(path);

            pendingFileResolver.resolve();
          }

          this.#saveFileHistory([path]);
        })
        .catch((error) => {
          toast.error(`Failed to upload file: ${filename}`);
          logger.error(error.message);
        })
        .finally(() => {
          this.binaryUploads--;
        });
    };

    const { changed, files, sizeDelta } = applyFileUpdates({ ...this.files.get() }, events, handleBinaryFileUpdate);

    if (changed.size > 0) {
      this.files.set(files);
      this.#size += sizeDelta;
    }

    // check if those config files have been changed
    for (const path of ['.stackblitzrc', '.stackblitz/config.json']) {
      if (changed.has(addWorkDir(path))) {
        try {
          const file = files[path];

          let content: string | undefined;

          if (file && file.type === 'file') {
            content = file.content;
            this.stackBlitzResourceConfigFile.set(JSON.parse(content));

            break;
          } else {
            // file was deleted, revert to default
            this.stackBlitzResourceConfigFile.set({ corsProxy: true });
          }
        } catch {
          // ignore
        }
      }
    }
  }

  generateUniqueName(filePath: string, kind: 'file' | 'folder'): string {
    const fullPath = addWorkDir(filePath);

    const isUnique =
      kind === 'file'
        ? this.getFile(fullPath) == null
        : Object.keys(this.files.get()).every((path) => path !== fullPath && !path.startsWith(`${fullPath}/`));

    if (!isUnique) {
      let extensionIndex = filePath.lastIndexOf('.');
      extensionIndex = extensionIndex > 0 ? extensionIndex : filePath.length;

      filePath = filePath.substring(0, extensionIndex) + ' copy' + filePath.substring(extensionIndex);

      return this.generateUniqueName(filePath, kind);
    }

    return filePath;
  }
}

function isHiddenFolder(path: string) {
  const parts = path.split(nodePath.sep);
  return parts.some((part) => part.startsWith('.') && !HIDDEN_FOLDERS_NOT_IGNORED.includes(part));
}

interface FileWithPath extends globalThis.File {
  path: string;
}

export interface Blob {
  signedId: string;
  content: string;
  name: string;
}

export async function uploadBinaryFile(
  slug: string,
  name: string,
  file: FileWithPath,
  onUpload?: PromiseWithResolvers<void>,
) {
  const token = await getToken();

  const promise = new Promise<Blob>((resolve, reject) => {
    const upload = new DirectUpload(
      file,
      `${CLIENT_ORIGIN}/api/projects/${slug}/direct_uploads`,
      {
        directUploadWillCreateBlobWithXHR(request) {
          request.addEventListener('error', (event) => {
            const target = event.target as XMLHttpRequest;
            reject(target);
          });
          request.addEventListener('loadend', (event) => {
            const target = event.target as XMLHttpRequest;

            if (target.status >= 400) {
              reject(target);
            }
          });
        },
        directUploadWillStoreFileWithXHR() {
          onUpload?.resolve();
        },
      },
      {
        Authorization: `Bearer ${token}`,
      },
    );

    upload.create((error, blob) => {
      if (error) {
        // we prefer to handle errors in the listeners above
        setTimeout(() => {
          reject(error);
        });
      } else {
        resolve({
          signedId: blob.signed_id,
          content: `${CLIENT_ORIGIN}/storage/blobs/${blob.signed_id}/${name}`,
          name,
        });
      }
    });
  });

  promise.catch((error) => onUpload?.reject(error));

  return promise;
}

function insertIntoTree(tree: FileSystemTree, path: string, node: FileSystemTree[keyof FileSystemTree]) {
  const components = path.split('/');

  let dir = tree;

  for (const [i, component] of components.entries()) {
    const leaf = i === components.length - 1;

    if (leaf) {
      dir[component] = node;
    } else {
      dir[component] ??= { directory: {} };

      dir = (dir[component] as any).directory;
    }
  }
}
